import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Calendar as CalendarIcon,
  Clock,
  Flag,
  Database,
  Settings,
  Server,
  Activity,
  Rocket,
  Sparkles,
  Search,
  GitCompare,
  Globe,
  LogOut,
  User,
  Users,
  Shield,
  HeartPulse,
  Link2,
  FileText,
  BarChart3,
  Zap,
  RotateCcw,
  Layers,
  Radio,
  Download,
  Ship,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getPagePermissions } from '../services/pagePermissionsApi';

const InactiveUsersIcon = ({ size = 18, ...props }) => (
  <div className="relative inline-flex" {...props}>
    <Users size={size} />
    <Clock size={size * 0.55} className="absolute -bottom-1 -right-1 text-gray-400" />
  </div>
);

const ALL_NAV_ITEMS = [
  { id: 'calendar', path: '/', label: 'Calendar', icon: CalendarIcon, permissionKey: 'calendar' },
  { id: 'feature-flags', path: '/feature-flags', label: 'Feature Flags', icon: Flag, permissionKey: 'featureFlags' },
  { id: 'query-vault', path: '/query-vault', label: 'QueryVault', icon: Database, permissionKey: 'queryVault' },
  { id: 'azure-vars', path: '/azure-variables', label: 'Azure Variables', icon: Settings, permissionKey: 'azureVariables' },
  { id: 'provisioning', path: '/tenant-provisioning', label: 'Tenant Provisioning', icon: Server, permissionKey: 'tenantProvisioning' },
  { id: 'onboarding', path: '/tenant-onboarding', label: 'Tenant Onboarding', icon: Server, permissionKey: 'tenantOnboarding' },
  { id: 'env-scanner', path: '/env-scanner', label: 'Env Scanner', icon: Activity, permissionKey: 'envScanner' },
  { id: 'env-inspector', path: '/env-inspector', label: 'Env Inspector', icon: Search, permissionKey: 'envScanner' },
  { id: 'env-compare', path: '/env-compare', label: 'Env Compare', icon: GitCompare, permissionKey: 'envScanner' },
  { id: 'cors-manager', path: '/cors-manager', label: 'CORS Manager', icon: Globe, permissionKey: 'envScanner' },
  { id: 'env-injector', path: '/env-injector', label: 'Env Injector', icon: Zap, permissionKey: 'envInjector' },
  { id: 'env-manager', path: '/env-manager', label: 'Env Manager', icon: Layers, permissionKey: 'envInjector' },
  { id: 'deployments', path: '/deployments', label: 'Deployments', icon: Rocket, permissionKey: 'deployments' },
  { id: 'deployments-beta', path: '/deployments-beta', label: 'Deployments (Beta)', icon: Ship, permissionKey: 'deployments' },
  { id: 'extra-deployments', path: '/extra-deployments', label: 'Extra Deployment', icon: Sparkles, permissionKey: null, devsuOnly: true },
  { id: 'inactive-users', path: '/inactive-users', label: 'Inactive Users', icon: InactiveUsersIcon, permissionKey: 'inactiveUsers' },
  { id: 'health-check', path: '/health-check', label: 'Health Check', icon: HeartPulse, permissionKey: 'healthCheck' },
  { id: 'url-registry', path: '/url-registry', label: 'URL Registry', icon: Link2, permissionKey: 'urlRegistry' },
  { id: 'avalara', path: '/avalara-licensing', label: 'Avalara Licensing', icon: FileText, permissionKey: 'avalaraLicensing' },
  { id: 'event-dashboard', path: '/event-dashboard', label: 'Event Dashboard', icon: BarChart3, permissionKey: 'eventDashboard' },
  { id: 'event-watcher', path: '/event-watcher', label: 'Event Watcher', icon: Radio, permissionKey: 'eventWatcher' },
  { id: 'training-revert', path: '/training-revert', label: 'Training Revert', icon: RotateCcw, permissionKey: 'trainingRevert' },
  { id: 'database-export', path: '/database-export', label: 'Database Export', icon: Download, permissionKey: 'databaseExport' },
  { id: 'access-control', path: '/access-control', label: 'Access Control', icon: Shield, permissionKey: null, adminOnly: true },
];

const Sidebar = ({ isMobileOpen, setIsMobileOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, domain, isAdmin, logout } = useAuth();
  const [permissions, setPermissions] = useState(null);

  const DEFAULT_PERMISSIONS = { calendar: true, featureFlags: true, queryVault: true, inactiveUsers: true, healthCheck: true, urlRegistry: true, envInjector: false };

  useEffect(() => {
    if (isAdmin) {
      setPermissions(null);
      return;
    }
    getPagePermissions()
      .then(setPermissions)
      .catch((err) => {
        if (err.status === 401) {
          logout();
        } else {
          setPermissions(DEFAULT_PERMISSIONS);
        }
      });
  }, [isAdmin, logout]);

  const navItems = isAdmin
    ? ALL_NAV_ITEMS.filter((item) => !item.devsuOnly || domain === 'dev.su')
    : ALL_NAV_ITEMS.filter((item) => {
        if (item.adminOnly) return false;
        if (item.devsuOnly) return domain === 'dev.su';
        if (!permissions) {
          const defaults = { calendar: true, featureFlags: true, queryVault: true, inactiveUsers: true, healthCheck: true, urlRegistry: true, avalaraLicensing: false, eventDashboard: false, envInjector: false };
          return defaults[item.permissionKey] === true;
        }
        return permissions[item.permissionKey] === true;
      });

  const handleLogout = () => {
    logout();
    navigate('/login');
    setIsMobileOpen(false);
  };

  return (
    <>
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-gray-900/50 z-20 lg:hidden"
          onClick={() => setIsMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        className={`
          fixed top-0 left-0 z-30 h-screen w-64 bg-slate-900 text-white flex flex-col transition-transform duration-300 ease-in-out
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Logo */}
        <div className="flex items-center h-16 px-6 border-b border-slate-800 shrink-0">
          <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
            <span className="font-bold text-white">I</span>
          </div>
          <span className="text-lg font-bold tracking-wide">Innova</span>
        </div>

        {/* Nav — scrollable so items never overlap the footer */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.id}
                to={item.path}
                onClick={() => setIsMobileOpen(false)}
                className={`
                  flex items-center w-full px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
                  ${isActive
                    ? 'bg-blue-600 text-white'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-white'}
                `}
              >
                <item.icon className="w-5 h-5 mr-3 shrink-0" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* User footer — always pinned to bottom, never overlapped */}
        <div className="shrink-0 p-4 border-t border-slate-800">
          <div className="flex items-center p-2 rounded-lg hover:bg-slate-800 cursor-pointer transition-colors">
            <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-slate-300 shrink-0">
              <User size={16} />
            </div>
            <div className="ml-3 flex-1 overflow-hidden min-w-0">
              <p className="text-sm font-medium text-white truncate">{user || 'User'}</p>
              <p className="text-xs text-slate-500 truncate">{user && domain ? `${user}@${domain}` : ''}</p>
            </div>
            <button
              onClick={handleLogout}
              className="p-1 text-slate-500 hover:text-red-400 transition-colors"
              aria-label="Logout"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
